import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Reveal } from './components/Reveal';
import { Button } from './components/ui/button';
import { Card, CardContent } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import {
  Truck,
  User,
  MapPin,
  Target,
  Calendar,
  Package,
  ChevronRight,
  ChevronLeft,
  ArrowLeft,
  Check,
  Plus,
  Trash2,
  Info,
  Clock,
  Shield,
  Globe,
  Tag,
  AlertTriangle,
  Send,
  CheckCircle2,
  Home
} from 'lucide-react';
import './App.css';

const US_STATES = [
  'Alabama','Alaska','American Samoa','Arizona','Arkansas','California','Colorado','Connecticut','Delaware',
  'District of Columbia','Florida','Georgia','Guam','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas',
  'Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri',
  'Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina',
  'North Dakota','Northern Mariana Islands','Ohio','Oklahoma','Oregon','Pennsylvania','Puerto Rico',
  'Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','U.S. Virgin Islands','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming','Armed Forces Americas','Armed Forces Europe',
  'Armed Forces Pacific'
];

const COUNTRIES = ['United States', 'Canada', 'Mexico'];

const UNIT_TYPES = [
  'Pallet','Skid','Bag','Bale','Barrel','Basket','Bin','Box','Bunch','Bundle','Cabinet','Can','Carboy',
  'Carrier','Carton','Case','Cask','Container','Crate','Cylinder','Drum','Jerrican','Loose','Package',
  'Pail','Pieces','Pipe Line','Reel','Roll','Spool','Tank','Totes','Tub','Tube','Unit','Van Pack','Wrapped','Other'
];

const FREIGHT_CLASSES = ['50','55','60','65','70','77.5','85','92.5','100','110','125','150','175','200','250','300','400','500'];

const EMPTY_UNIT = {
  numUnits: '1',
  type: '',
  stackable: '',
  length: '',
  width: '',
  height: '',
  totalWeight: '',
  freightClass: '',
  nmfc: '',
  hazmat: 'No',
  commodityDescription: '',
  pieceCount: '',
  pieceType: ''
};

const INITIAL_FORM = {
  companyName: '',
  accountNumber: 'U002998141',
  email: '',
  phone: '',
  originCompanyName: '',
  originAddress1: '',
  originAddress2: '',
  originCity: '',
  originState: '',
  originZip: '',
  originCountry: 'United States',
  originContactName: '',
  originContactPhone: '',
  originContactEmail: '',
  destCompanyName: '',
  destAddress1: '',
  destAddress2: '',
  destCity: '',
  destState: '',
  destZip: '',
  destCountry: 'United States',
  destContactName: '',
  destContactPhone: '',
  destContactEmail: '',
  readyDate: '',
  readyTime: '09:00',
  closeTime: '17:00',
  guaranteedDelivery: 'No',
  guaranteedDeliveryDate: '',
  guaranteedDeliveryTime: '',
  insurance: 'No',
  insuranceValue: '',
  conditionOfGoods: 'New Commercial Goods',
  international: 'No',
  brokerCompany: '',
  brokerContact: '',
  brokerPhone: '',
  referenceRequired: 'No',
  referenceNumber: '',
  originInsidePickup: false,
  originLiftgate: false,
  originResidential: false,
  originNonCommercial: false,
  originOther: false,
  originOtherText: '',
  destInsideDelivery: false,
  destLiftgate: false,
  destResidential: false,
  destAppointment: false,
  destOther: false,
  destOtherText: '',
  notes: ''
};

const STEPS = [
  { id: 1, title: 'Contact Info', icon: User },
  { id: 2, title: 'Origin & Destination', icon: MapPin },
  { id: 3, title: 'Shipment Details', icon: Calendar },
  { id: 4, title: 'Items & Review', icon: Package }
];

export default function ShippingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [handlingUnits, setHandlingUnits] = useState([{ ...EMPTY_UNIT }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState('');
  const [errors, setErrors] = useState({});

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
    }
  };

  const updateUnit = (index, field, value) => {
    setHandlingUnits(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addUnit = () => {
    if (handlingUnits.length < 20) {
      setHandlingUnits(prev => [...prev, { ...EMPTY_UNIT }]);
    }
  };

  const removeUnit = (index) => {
    if (handlingUnits.length > 1) {
      setHandlingUnits(prev => prev.filter((_, i) => i !== index));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 1) {
      if (!formData.companyName.trim()) newErrors.companyName = 'Required';
      if (!formData.email.trim()) newErrors.email = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
      if (!formData.phone.trim()) newErrors.phone = 'Required';
    }
    if (step === 2) {
      if (!formData.originCompanyName.trim()) newErrors.originCompanyName = 'Required';
      if (!formData.originAddress1.trim()) newErrors.originAddress1 = 'Required';
      if (!formData.originCity.trim()) newErrors.originCity = 'Required';
      if (!formData.originState) newErrors.originState = 'Required';
      if (!formData.originZip.trim()) newErrors.originZip = 'Required';
      if (!formData.originContactName.trim()) newErrors.originContactName = 'Required';
      if (!formData.originContactPhone.trim()) newErrors.originContactPhone = 'Required';
      if (!formData.destCompanyName.trim()) newErrors.destCompanyName = 'Required';
      if (!formData.destAddress1.trim()) newErrors.destAddress1 = 'Required';
      if (!formData.destCity.trim()) newErrors.destCity = 'Required';
      if (!formData.destState) newErrors.destState = 'Required';
      if (!formData.destZip.trim()) newErrors.destZip = 'Required';
      if (!formData.destContactName.trim()) newErrors.destContactName = 'Required';
    }
    if (step === 3) {
      if (!formData.readyDate) newErrors.readyDate = 'Required';
      if (!formData.readyTime) newErrors.readyTime = 'Required';
      if (!formData.closeTime) newErrors.closeTime = 'Required';
    }
    if (step === 4) {
      handlingUnits.forEach((unit, i) => {
        if (!unit.type) newErrors[`unit_${i}_type`] = 'Required';
        if (!unit.stackable) newErrors[`unit_${i}_stackable`] = 'Required';
        if (!unit.length) newErrors[`unit_${i}_length`] = 'Required';
        if (!unit.width) newErrors[`unit_${i}_width`] = 'Required';
        if (!unit.height) newErrors[`unit_${i}_height`] = 'Required';
        if (!unit.totalWeight) newErrors[`unit_${i}_totalWeight`] = 'Required';
        if (!unit.freightClass) newErrors[`unit_${i}_freightClass`] = 'Required';
        if (!unit.commodityDescription.trim()) newErrors[`unit_${i}_commodityDescription`] = 'Required';
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      toast.error('Please fill in all required fields', {
        description: 'Check the highlighted fields above.'
      });
    }
  };

  const goBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const payload = {
        ...formData,
        handlingUnits: handlingUnits.map(u => ({
          ...u,
          numUnits: parseInt(u.numUnits) || 1,
          length: parseFloat(u.length) || 0,
          width: parseFloat(u.width) || 0,
          height: parseFloat(u.height) || 0,
          totalWeight: parseFloat(u.totalWeight) || 0
        }))
      };
      const response = await fetch(`${backendUrl}/api/shipment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to submit');
      const data = await response.json();
      setSubmissionId(data.id);
      setIsSubmitted(true);
      toast.success('Shipment request submitted!', {
        description: 'You will receive a confirmation email shortly.'
      });
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Failed to submit shipment request', {
        description: 'Please try again or contact us at Info@2ticorp.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const FieldError = ({ field }) => errors[field] ? (
    <span className="text-sm text-destructive mt-1">{errors[field]}</span>
  ) : null;

  const RequiredStar = () => <span className="text-[#C9A227] ml-0.5">*</span>;

  const RadioOption = ({ value, selected, onChange, label }) => (
    <button
      type="button"
      onClick={() => onChange(value)}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-150 ${
        selected === value
          ? 'border-[#C9A227] bg-[#C9A227]/10 text-[#0A1B2E]'
          : 'border-border bg-card hover:border-[#C9A227]/50 text-muted-foreground'
      }`}
    >
      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
        selected === value ? 'border-[#C9A227]' : 'border-muted-foreground/40'
      }`}>
        {selected === value && <div className="w-2 h-2 rounded-full bg-[#C9A227]" />}
      </div>
      {label}
    </button>
  );

  const CheckboxOption = ({ checked, onChange, label }) => (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all duration-150 ${
        checked
          ? 'border-[#C9A227] bg-[#C9A227]/10 text-[#0A1B2E]'
          : 'border-border bg-card hover:border-[#C9A227]/50 text-muted-foreground'
      }`}
    >
      <div className={`w-4 h-4 rounded flex items-center justify-center border-2 ${
        checked ? 'border-[#C9A227] bg-[#C9A227]' : 'border-muted-foreground/40'
      }`}>
        {checked && <Check className="w-3 h-3 text-white" />}
      </div>
      {label}
    </button>
  );

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background">
        <Toaster richColors position="top-right" />
        <header className="fixed top-0 inset-x-0 z-50 bg-[rgba(252,251,247,0.9)] backdrop-blur-sm border-b border-border">
          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 h-16 flex items-center">
            <Link to="/" className="flex items-center gap-3" data-testid="shipping-logo-link">
              <div className="w-12 h-12 bg-[#0A1B2E] rounded-xl p-2 flex items-center justify-center">
                <img src="https://customer-assets.emergentagent.com/job_db9d9eeb-eba2-43bb-aace-3ceec14de46a/artifacts/nn1cqftm_2TLogo.png" alt="2TI Corp Logo" className="w-full h-full object-contain" />
              </div>
              <div>
                <span className="font-chivo font-bold text-[#0A1B2E] text-xl block leading-none">2TI CORP</span>
                <span className="text-[#C9A227] text-xs font-medium tracking-wider">BUSINESS SOLUTIONS</span>
              </div>
            </Link>
          </div>
        </header>

        <main className="pt-16 min-h-screen flex items-center justify-center px-4">
          <Reveal>
            <Card className="max-w-lg w-full border-border shadow-lg" data-testid="submission-success-card">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-[#C9A227]/15 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-[#C9A227]" />
                </div>
                <h2 className="font-chivo text-2xl font-bold text-foreground mb-3">Shipment Request Submitted!</h2>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  Your freight booking request has been received and is being processed. Our logistics team will review your shipment details and reach out within one business day.
                </p>
                <div className="bg-muted/50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-muted-foreground">Reference ID</p>
                  <p className="font-mono text-sm font-semibold text-foreground" data-testid="submission-reference-id">{submissionId}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="gap-2"
                    data-testid="back-to-home-btn"
                  >
                    <Home className="w-4 h-4" /> Back to Home
                  </Button>
                  <Button
                    onClick={() => { setIsSubmitted(false); setCurrentStep(1); setFormData(INITIAL_FORM); setHandlingUnits([{ ...EMPTY_UNIT }]); }}
                    className="bg-[#C9A227] text-[#0A1B2E] hover:bg-[#B69121] gap-2"
                    data-testid="new-shipment-btn"
                  >
                    <Truck className="w-4 h-4" /> New Shipment Request
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="shipping-page">
      <Toaster richColors position="top-right" />

      {/* Shipping Page Header */}
      <header className="fixed top-0 inset-x-0 z-50 bg-[rgba(252,251,247,0.9)] backdrop-blur-sm border-b border-border" data-testid="shipping-header">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 transition-transform hover:scale-105 duration-150" data-testid="shipping-logo-link">
            <div className="w-12 h-12 bg-[#0A1B2E] rounded-xl p-2 flex items-center justify-center">
              <img src="https://customer-assets.emergentagent.com/job_db9d9eeb-eba2-43bb-aace-3ceec14de46a/artifacts/nn1cqftm_2TLogo.png" alt="2TI Corp Logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <span className="font-chivo font-bold text-[#0A1B2E] text-xl block leading-none">2TI CORP</span>
              <span className="text-[#C9A227] text-xs font-medium tracking-wider">BUSINESS SOLUTIONS</span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150"
              data-testid="shipping-back-home-link"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="sm:hidden"
              data-testid="shipping-back-home-btn-mobile"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-16">
        {/* Hero Banner */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0A1B2E] via-[#0F2742] to-[#0A1B2E] text-white" data-testid="shipping-hero">
          <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(ellipse at 30% 20%, rgba(201,162,39,0.15) 0%, transparent 60%)' }} />
          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative text-center">
            <Badge className="bg-[#C9A227]/15 text-[#C9A227] border-[#C9A227]/30 hover:bg-[#C9A227]/20 mb-4 gap-2" data-testid="shipping-badge">
              <Truck className="w-4 h-4" /> FREIGHT BOOKING REQUEST
            </Badge>
            <h1 className="font-chivo text-3xl sm:text-4xl lg:text-5xl font-black mb-4">
              Request a <span className="text-[#C9A227]">Shipment</span>
            </h1>
            <p className="text-white/70 text-base sm:text-lg max-w-xl mx-auto">
              Fill out the form below and we'll get your freight booked with competitive rates through our nationwide carrier network.
            </p>
          </div>
        </section>

        {/* Form Container */}
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 -mt-8 relative z-10 pb-16">
          {/* Step Indicator */}
          <Card className="mb-6 border-border shadow-sm" data-testid="step-indicator">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                {STEPS.map((step, idx) => (
                  <React.Fragment key={step.id}>
                    <button
                      type="button"
                      onClick={() => { if (step.id < currentStep) setCurrentStep(step.id); }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        step.id === currentStep
                          ? 'bg-[#0A1B2E] text-[#C9A227]'
                          : step.id < currentStep
                            ? 'text-foreground cursor-pointer hover:bg-muted'
                            : 'text-muted-foreground cursor-default'
                      }`}
                      data-testid={`step-${step.id}-btn`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        step.id === currentStep
                          ? 'bg-[#C9A227] text-[#0A1B2E]'
                          : step.id < currentStep
                            ? 'bg-green-600 text-white'
                            : 'bg-muted text-muted-foreground'
                      }`}>
                        {step.id < currentStep ? <Check className="w-3.5 h-3.5" /> : step.id}
                      </div>
                      <span className="hidden sm:inline">{step.title}</span>
                    </button>
                    {idx < STEPS.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 rounded ${step.id < currentStep ? 'bg-green-600' : 'bg-muted'}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Info Box */}
          <div className="flex items-start gap-3 bg-[#C9A227]/8 border border-[#C9A227]/20 rounded-xl px-4 py-3 mb-6" data-testid="shipping-info-box">
            <Info className="w-5 h-5 text-[#C9A227] mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              Please fill in all required fields marked with <span className="text-[#C9A227] font-semibold">*</span>. Your shipment request will be processed and you'll receive a confirmation within one business day.
            </p>
          </div>

          {/* STEP 1: Contact Info */}
          {currentStep === 1 && (
            <Reveal>
              <Card className="border-border shadow-sm" data-testid="step-1-form">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0F2742] to-[#173654] flex items-center justify-center">
                      <User className="w-5 h-5 text-[#C9A227]" />
                    </div>
                    <div>
                      <h2 className="font-chivo text-xl font-bold text-foreground">Contact Information</h2>
                      <p className="text-sm text-muted-foreground">Your company and contact details</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Company Name<RequiredStar /></Label>
                      <Input
                        value={formData.companyName}
                        onChange={(e) => updateField('companyName', e.target.value)}
                        placeholder="Enter company name"
                        className={errors.companyName ? 'border-destructive' : ''}
                        data-testid="field-companyName"
                      />
                      <FieldError field="companyName" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Email<RequiredStar /></Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        placeholder="your@email.com"
                        className={errors.email ? 'border-destructive' : ''}
                        data-testid="field-email"
                      />
                      <FieldError field="email" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Phone<RequiredStar /></Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => updateField('phone', e.target.value)}
                        placeholder="(555) 123-4567"
                        className={errors.phone ? 'border-destructive' : ''}
                        data-testid="field-phone"
                      />
                      <FieldError field="phone" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          )}

          {/* STEP 2: Origin & Destination */}
          {currentStep === 2 && (
            <Reveal>
              <div className="space-y-6">
                {/* Origin */}
                <Card className="border-border shadow-sm" data-testid="step-2-origin-form">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0F2742] to-[#173654] flex items-center justify-center">
                        <Target className="w-5 h-5 text-[#C9A227]" />
                      </div>
                      <div>
                        <h2 className="font-chivo text-xl font-bold text-foreground">Origin</h2>
                        <p className="text-sm text-muted-foreground">Where the shipment is picked up</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Company Name<RequiredStar /></Label>
                        <Input value={formData.originCompanyName} onChange={(e) => updateField('originCompanyName', e.target.value)} placeholder="Origin company" className={errors.originCompanyName ? 'border-destructive' : ''} data-testid="field-originCompanyName" />
                        <FieldError field="originCompanyName" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Contact Name<RequiredStar /></Label>
                        <Input value={formData.originContactName} onChange={(e) => updateField('originContactName', e.target.value)} placeholder="Contact person" className={errors.originContactName ? 'border-destructive' : ''} data-testid="field-originContactName" />
                        <FieldError field="originContactName" />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label className="text-sm font-semibold">Address Line 1<RequiredStar /></Label>
                        <Input value={formData.originAddress1} onChange={(e) => updateField('originAddress1', e.target.value)} placeholder="Street address" className={errors.originAddress1 ? 'border-destructive' : ''} data-testid="field-originAddress1" />
                        <FieldError field="originAddress1" />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label className="text-sm font-semibold">Address Line 2</Label>
                        <Input value={formData.originAddress2} onChange={(e) => updateField('originAddress2', e.target.value)} placeholder="Suite, unit, building (optional)" data-testid="field-originAddress2" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">City<RequiredStar /></Label>
                        <Input value={formData.originCity} onChange={(e) => updateField('originCity', e.target.value)} placeholder="City" className={errors.originCity ? 'border-destructive' : ''} data-testid="field-originCity" />
                        <FieldError field="originCity" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">State<RequiredStar /></Label>
                        <Select value={formData.originState} onValueChange={(v) => updateField('originState', v)}>
                          <SelectTrigger className={errors.originState ? 'border-destructive' : ''} data-testid="field-originState">
                            <SelectValue placeholder="Select State" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <FieldError field="originState" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">ZIP Code<RequiredStar /></Label>
                        <Input value={formData.originZip} onChange={(e) => updateField('originZip', e.target.value)} placeholder="92705" className={errors.originZip ? 'border-destructive' : ''} data-testid="field-originZip" />
                        <FieldError field="originZip" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Country<RequiredStar /></Label>
                        <Select value={formData.originCountry} onValueChange={(v) => updateField('originCountry', v)}>
                          <SelectTrigger data-testid="field-originCountry"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Contact Phone<RequiredStar /></Label>
                        <Input type="tel" value={formData.originContactPhone} onChange={(e) => updateField('originContactPhone', e.target.value)} placeholder="(555) 123-4567" className={errors.originContactPhone ? 'border-destructive' : ''} data-testid="field-originContactPhone" />
                        <FieldError field="originContactPhone" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Contact Email</Label>
                        <Input type="email" value={formData.originContactEmail} onChange={(e) => updateField('originContactEmail', e.target.value)} placeholder="contact@company.com" data-testid="field-originContactEmail" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Destination */}
                <Card className="border-border shadow-sm" data-testid="step-2-dest-form">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0F2742] to-[#173654] flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-[#C9A227]" />
                      </div>
                      <div>
                        <h2 className="font-chivo text-xl font-bold text-foreground">Destination</h2>
                        <p className="text-sm text-muted-foreground">Where the shipment is delivered</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Company Name<RequiredStar /></Label>
                        <Input value={formData.destCompanyName} onChange={(e) => updateField('destCompanyName', e.target.value)} placeholder="Destination company" className={errors.destCompanyName ? 'border-destructive' : ''} data-testid="field-destCompanyName" />
                        <FieldError field="destCompanyName" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Contact Name<RequiredStar /></Label>
                        <Input value={formData.destContactName} onChange={(e) => updateField('destContactName', e.target.value)} placeholder="Contact person" className={errors.destContactName ? 'border-destructive' : ''} data-testid="field-destContactName" />
                        <FieldError field="destContactName" />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label className="text-sm font-semibold">Address Line 1<RequiredStar /></Label>
                        <Input value={formData.destAddress1} onChange={(e) => updateField('destAddress1', e.target.value)} placeholder="Street address" className={errors.destAddress1 ? 'border-destructive' : ''} data-testid="field-destAddress1" />
                        <FieldError field="destAddress1" />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label className="text-sm font-semibold">Address Line 2</Label>
                        <Input value={formData.destAddress2} onChange={(e) => updateField('destAddress2', e.target.value)} placeholder="Suite, unit, building (optional)" data-testid="field-destAddress2" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">City<RequiredStar /></Label>
                        <Input value={formData.destCity} onChange={(e) => updateField('destCity', e.target.value)} placeholder="City" className={errors.destCity ? 'border-destructive' : ''} data-testid="field-destCity" />
                        <FieldError field="destCity" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">State<RequiredStar /></Label>
                        <Select value={formData.destState} onValueChange={(v) => updateField('destState', v)}>
                          <SelectTrigger className={errors.destState ? 'border-destructive' : ''} data-testid="field-destState">
                            <SelectValue placeholder="Select State" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <FieldError field="destState" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">ZIP Code<RequiredStar /></Label>
                        <Input value={formData.destZip} onChange={(e) => updateField('destZip', e.target.value)} placeholder="10001" className={errors.destZip ? 'border-destructive' : ''} data-testid="field-destZip" />
                        <FieldError field="destZip" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Country<RequiredStar /></Label>
                        <Select value={formData.destCountry} onValueChange={(v) => updateField('destCountry', v)}>
                          <SelectTrigger data-testid="field-destCountry"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Contact Phone</Label>
                        <Input type="tel" value={formData.destContactPhone} onChange={(e) => updateField('destContactPhone', e.target.value)} placeholder="(555) 123-4567" data-testid="field-destContactPhone" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Contact Email</Label>
                        <Input type="email" value={formData.destContactEmail} onChange={(e) => updateField('destContactEmail', e.target.value)} placeholder="contact@company.com" data-testid="field-destContactEmail" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </Reveal>
          )}

          {/* STEP 3: Shipment Details */}
          {currentStep === 3 && (
            <Reveal>
              <Card className="border-border shadow-sm" data-testid="step-3-form">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0F2742] to-[#173654] flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-[#C9A227]" />
                    </div>
                    <div>
                      <h2 className="font-chivo text-xl font-bold text-foreground">Shipment Information</h2>
                      <p className="text-sm text-muted-foreground">Scheduling, insurance, and service options</p>
                    </div>
                  </div>

                  {/* Carrier time notice */}
                  <div className="flex items-start gap-3 bg-muted/50 border border-border rounded-xl px-4 py-3 mb-6">
                    <Clock className="w-5 h-5 text-[#C9A227] mt-0.5 shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      Carriers require a two-hour span between ready and close times. Same-day pickups after 3 PM may be pushed to the next business day.
                    </p>
                  </div>

                  {/* Schedule */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Ready Date<RequiredStar /></Label>
                      <Input type="date" value={formData.readyDate} onChange={(e) => updateField('readyDate', e.target.value)} className={errors.readyDate ? 'border-destructive' : ''} data-testid="field-readyDate" />
                      <FieldError field="readyDate" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Ready Time<RequiredStar /></Label>
                      <Input type="time" value={formData.readyTime} onChange={(e) => updateField('readyTime', e.target.value)} className={errors.readyTime ? 'border-destructive' : ''} data-testid="field-readyTime" />
                      <FieldError field="readyTime" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Close Time<RequiredStar /></Label>
                      <Input type="time" value={formData.closeTime} onChange={(e) => updateField('closeTime', e.target.value)} className={errors.closeTime ? 'border-destructive' : ''} data-testid="field-closeTime" />
                      <FieldError field="closeTime" />
                    </div>
                  </div>

                  {/* Options Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-3">
                      <Label className="text-sm font-semibold flex items-center gap-2"><Calendar className="w-4 h-4 text-muted-foreground" /> Guaranteed Delivery Date?</Label>
                      <div className="flex gap-2">
                        <RadioOption value="Yes" selected={formData.guaranteedDelivery} onChange={(v) => updateField('guaranteedDelivery', v)} label="Yes" />
                        <RadioOption value="No" selected={formData.guaranteedDelivery} onChange={(v) => updateField('guaranteedDelivery', v)} label="No" />
                      </div>
                      {formData.guaranteedDelivery === 'Yes' && (
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <Input type="date" value={formData.guaranteedDeliveryDate} onChange={(e) => updateField('guaranteedDeliveryDate', e.target.value)} placeholder="Date" data-testid="field-guaranteedDeliveryDate" />
                          <Input type="time" value={formData.guaranteedDeliveryTime} onChange={(e) => updateField('guaranteedDeliveryTime', e.target.value)} placeholder="Time" data-testid="field-guaranteedDeliveryTime" />
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <Label className="text-sm font-semibold flex items-center gap-2"><Shield className="w-4 h-4 text-muted-foreground" /> Shipment Insurance?</Label>
                      <div className="flex gap-2">
                        <RadioOption value="Yes" selected={formData.insurance} onChange={(v) => updateField('insurance', v)} label="Yes" />
                        <RadioOption value="No" selected={formData.insurance} onChange={(v) => updateField('insurance', v)} label="No" />
                      </div>
                      {formData.insurance === 'Yes' && (
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Value ($)</Label>
                            <Input value={formData.insuranceValue} onChange={(e) => updateField('insuranceValue', e.target.value)} placeholder="0.00" data-testid="field-insuranceValue" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Condition</Label>
                            <Select value={formData.conditionOfGoods} onValueChange={(v) => updateField('conditionOfGoods', v)}>
                              <SelectTrigger data-testid="field-conditionOfGoods"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="New Commercial Goods">New Commercial</SelectItem>
                                <SelectItem value="Used, Rebuilt or Refurbished Goods">Used/Refurbished</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                    <div className="space-y-3">
                      <Label className="text-sm font-semibold flex items-center gap-2"><Globe className="w-4 h-4 text-muted-foreground" /> International Shipment?</Label>
                      <div className="flex gap-2">
                        <RadioOption value="Yes" selected={formData.international} onChange={(v) => updateField('international', v)} label="Yes" />
                        <RadioOption value="No" selected={formData.international} onChange={(v) => updateField('international', v)} label="No" />
                      </div>
                      {formData.international === 'Yes' && (
                        <div className="space-y-3 mt-2">
                          <Input value={formData.brokerCompany} onChange={(e) => updateField('brokerCompany', e.target.value)} placeholder="Broker company" data-testid="field-brokerCompany" />
                          <Input value={formData.brokerContact} onChange={(e) => updateField('brokerContact', e.target.value)} placeholder="Broker contact" data-testid="field-brokerContact" />
                          <Input value={formData.brokerPhone} onChange={(e) => updateField('brokerPhone', e.target.value)} placeholder="Broker phone" data-testid="field-brokerPhone" />
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <Label className="text-sm font-semibold flex items-center gap-2"><Tag className="w-4 h-4 text-muted-foreground" /> Reference Required at Pickup?</Label>
                      <div className="flex gap-2">
                        <RadioOption value="Yes" selected={formData.referenceRequired} onChange={(v) => updateField('referenceRequired', v)} label="Yes" />
                        <RadioOption value="No" selected={formData.referenceRequired} onChange={(v) => updateField('referenceRequired', v)} label="No" />
                      </div>
                      {formData.referenceRequired === 'Yes' && (
                        <Input value={formData.referenceNumber} onChange={(e) => updateField('referenceNumber', e.target.value)} placeholder="Reference number" className="mt-2" data-testid="field-referenceNumber" />
                      )}
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Service Options */}
                  <div className="space-y-6">
                    <div>
                      <Label className="text-sm font-semibold mb-3 block">Origin Services</Label>
                      <div className="flex flex-wrap gap-2">
                        <CheckboxOption checked={formData.originInsidePickup} onChange={(v) => updateField('originInsidePickup', v)} label="Inside Pickup" />
                        <CheckboxOption checked={formData.originLiftgate} onChange={(v) => updateField('originLiftgate', v)} label="Liftgate at Pickup" />
                        <CheckboxOption checked={formData.originResidential} onChange={(v) => updateField('originResidential', v)} label="Residential Pickup" />
                        <CheckboxOption checked={formData.originNonCommercial} onChange={(v) => updateField('originNonCommercial', v)} label="Non-Commercial" />
                        <CheckboxOption checked={formData.originOther} onChange={(v) => updateField('originOther', v)} label="Other" />
                      </div>
                      {formData.originOther && (
                        <Input value={formData.originOtherText} onChange={(e) => updateField('originOtherText', e.target.value)} placeholder="Describe other origin service" className="mt-3" data-testid="field-originOtherText" />
                      )}
                    </div>
                    <div>
                      <Label className="text-sm font-semibold mb-3 block">Destination Services</Label>
                      <div className="flex flex-wrap gap-2">
                        <CheckboxOption checked={formData.destInsideDelivery} onChange={(v) => updateField('destInsideDelivery', v)} label="Inside Delivery" />
                        <CheckboxOption checked={formData.destLiftgate} onChange={(v) => updateField('destLiftgate', v)} label="Liftgate at Delivery" />
                        <CheckboxOption checked={formData.destResidential} onChange={(v) => updateField('destResidential', v)} label="Residential Delivery" />
                        <CheckboxOption checked={formData.destAppointment} onChange={(v) => updateField('destAppointment', v)} label="Appointment Delivery" />
                        <CheckboxOption checked={formData.destOther} onChange={(v) => updateField('destOther', v)} label="Other" />
                      </div>
                      {formData.destOther && (
                        <Input value={formData.destOtherText} onChange={(e) => updateField('destOtherText', e.target.value)} placeholder="Describe other destination service" className="mt-3" data-testid="field-destOtherText" />
                      )}
                    </div>
                  </div>

                  <Separator className="my-6" />

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Notes / Additional References</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => updateField('notes', e.target.value)}
                      placeholder="Any special instructions, reference numbers, or additional details..."
                      rows={3}
                      data-testid="field-notes"
                    />
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          )}

          {/* STEP 4: Items & Review */}
          {currentStep === 4 && (
            <Reveal>
              <div className="space-y-6">
                <Card className="border-border shadow-sm" data-testid="step-4-form">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0F2742] to-[#173654] flex items-center justify-center">
                        <Package className="w-5 h-5 text-[#C9A227]" />
                      </div>
                      <div>
                        <h2 className="font-chivo text-xl font-bold text-foreground">Handling Units & Items</h2>
                        <p className="text-sm text-muted-foreground">Dimensions, weight, and freight class</p>
                      </div>
                    </div>

                    {handlingUnits.map((unit, idx) => (
                      <div key={idx} className="mb-6 last:mb-0">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-chivo text-sm font-bold text-muted-foreground uppercase tracking-wide">
                            Handling Unit {idx + 1}
                          </h3>
                          {handlingUnits.length > 1 && (
                            <Button variant="ghost" size="sm" onClick={() => removeUnit(idx)} className="text-destructive hover:text-destructive/80 gap-1" data-testid={`remove-unit-${idx}`}>
                              <Trash2 className="w-3.5 h-3.5" /> Remove
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                          <div className="space-y-1">
                            <Label className="text-xs font-semibold"># Units<RequiredStar /></Label>
                            <Input type="number" min="1" value={unit.numUnits} onChange={(e) => updateUnit(idx, 'numUnits', e.target.value)} data-testid={`field-unit-${idx}-numUnits`} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs font-semibold">Type<RequiredStar /></Label>
                            <Select value={unit.type} onValueChange={(v) => updateUnit(idx, 'type', v)}>
                              <SelectTrigger className={errors[`unit_${idx}_type`] ? 'border-destructive' : ''} data-testid={`field-unit-${idx}-type`}>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {UNIT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs font-semibold">Stackable<RequiredStar /></Label>
                            <Select value={unit.stackable} onValueChange={(v) => updateUnit(idx, 'stackable', v)}>
                              <SelectTrigger className={errors[`unit_${idx}_stackable`] ? 'border-destructive' : ''} data-testid={`field-unit-${idx}-stackable`}>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Yes">Yes</SelectItem>
                                <SelectItem value="No">No</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs font-semibold">Class<RequiredStar /></Label>
                            <Select value={unit.freightClass} onValueChange={(v) => updateUnit(idx, 'freightClass', v)}>
                              <SelectTrigger className={errors[`unit_${idx}_freightClass`] ? 'border-destructive' : ''} data-testid={`field-unit-${idx}-freightClass`}>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {FREIGHT_CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                          <div className="space-y-1">
                            <Label className="text-xs font-semibold">Length (in)<RequiredStar /></Label>
                            <Input type="number" value={unit.length} onChange={(e) => updateUnit(idx, 'length', e.target.value)} placeholder="48" className={errors[`unit_${idx}_length`] ? 'border-destructive' : ''} data-testid={`field-unit-${idx}-length`} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs font-semibold">Width (in)<RequiredStar /></Label>
                            <Input type="number" value={unit.width} onChange={(e) => updateUnit(idx, 'width', e.target.value)} placeholder="40" className={errors[`unit_${idx}_width`] ? 'border-destructive' : ''} data-testid={`field-unit-${idx}-width`} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs font-semibold">Height (in)<RequiredStar /></Label>
                            <Input type="number" value={unit.height} onChange={(e) => updateUnit(idx, 'height', e.target.value)} placeholder="48" className={errors[`unit_${idx}_height`] ? 'border-destructive' : ''} data-testid={`field-unit-${idx}-height`} />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs font-semibold">Weight (lbs)<RequiredStar /></Label>
                            <Input type="number" value={unit.totalWeight} onChange={(e) => updateUnit(idx, 'totalWeight', e.target.value)} placeholder="500" className={errors[`unit_${idx}_totalWeight`] ? 'border-destructive' : ''} data-testid={`field-unit-${idx}-totalWeight`} />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                          <div className="space-y-1">
                            <Label className="text-xs font-semibold">NMFC Code</Label>
                            <Input value={unit.nmfc} onChange={(e) => updateUnit(idx, 'nmfc', e.target.value)} placeholder="Optional" data-testid={`field-unit-${idx}-nmfc`} />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-muted-foreground" /> Hazmat?</Label>
                            <div className="flex gap-2">
                              <RadioOption value="Yes" selected={unit.hazmat} onChange={(v) => updateUnit(idx, 'hazmat', v)} label="Yes" />
                              <RadioOption value="No" selected={unit.hazmat} onChange={(v) => updateUnit(idx, 'hazmat', v)} label="No" />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-semibold">Commodity Description<RequiredStar /></Label>
                          <Textarea
                            value={unit.commodityDescription}
                            onChange={(e) => updateUnit(idx, 'commodityDescription', e.target.value)}
                            placeholder="Describe the items being shipped..."
                            rows={2}
                            className={errors[`unit_${idx}_commodityDescription`] ? 'border-destructive' : ''}
                            data-testid={`field-unit-${idx}-commodityDescription`}
                          />
                        </div>
                        {idx < handlingUnits.length - 1 && <Separator className="mt-6" />}
                      </div>
                    ))}

                    {handlingUnits.length < 20 && (
                      <Button variant="outline" onClick={addUnit} className="mt-4 gap-2 text-[#C9A227] border-[#C9A227]/30 hover:bg-[#C9A227]/10 hover:border-[#C9A227]/50" data-testid="add-unit-btn">
                        <Plus className="w-4 h-4" /> Add Handling Unit
                      </Button>
                    )}
                  </CardContent>
                </Card>

                {/* Review Summary */}
                <Card className="border-[#C9A227]/30 bg-[#C9A227]/5 shadow-sm" data-testid="review-summary">
                  <CardContent className="p-6 sm:p-8">
                    <h3 className="font-chivo text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-[#C9A227]" /> Review Summary
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Contact</h4>
                        <p className="text-muted-foreground">{formData.companyName}</p>
                        <p className="text-muted-foreground">{formData.email} | {formData.phone}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Schedule</h4>
                        <p className="text-muted-foreground">Date: {formData.readyDate || 'Not set'}</p>
                        <p className="text-muted-foreground">Time: {formData.readyTime} - {formData.closeTime}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Origin</h4>
                        <p className="text-muted-foreground">{formData.originCompanyName}</p>
                        <p className="text-muted-foreground">{formData.originAddress1}</p>
                        <p className="text-muted-foreground">{formData.originCity}, {formData.originState} {formData.originZip}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Destination</h4>
                        <p className="text-muted-foreground">{formData.destCompanyName}</p>
                        <p className="text-muted-foreground">{formData.destAddress1}</p>
                        <p className="text-muted-foreground">{formData.destCity}, {formData.destState} {formData.destZip}</p>
                      </div>
                    </div>
                    <Separator className="my-4" />
                    <p className="text-sm text-muted-foreground">
                      <strong>{handlingUnits.length}</strong> handling unit(s) | Insurance: {formData.insurance} | International: {formData.international}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </Reveal>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8" data-testid="wizard-nav-buttons">
            {currentStep > 1 ? (
              <Button variant="outline" onClick={goBack} className="gap-2" data-testid="wizard-back-btn">
                <ChevronLeft className="w-4 h-4" /> Back
              </Button>
            ) : (
              <Button variant="outline" onClick={() => navigate('/')} className="gap-2" data-testid="wizard-home-btn">
                <ArrowLeft className="w-4 h-4" /> Home
              </Button>
            )}
            {currentStep < 4 ? (
              <Button onClick={goNext} className="bg-[#C9A227] text-[#0A1B2E] hover:bg-[#B69121] gap-2 font-semibold" data-testid="wizard-next-btn">
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-[#C9A227] text-[#0A1B2E] hover:bg-[#B69121] gap-2 font-semibold px-8"
                data-testid="wizard-submit-btn"
              >
                {isSubmitting ? (
                  <>Submitting...</>
                ) : (
                  <><Send className="w-4 h-4" /> Submit Shipment Request</>
                )}
              </Button>
            )}
          </div>

          {/* Trust note */}
          <p className="text-center text-xs text-muted-foreground mt-6 flex items-center justify-center gap-2">
            <Shield className="w-3.5 h-3.5" /> Your data is securely processed and submitted to our freight partner network
          </p>
        </div>
      </main>
    </div>
  );
}
